import DocumentsData from '@/ts/DocumentsData';
import axios from 'axios';
import router from '@/router';
import store from '@/store';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Endpoints, Mutations, Actions } from '@/ts/store/Constants';
import { DocumentDataResponse } from '@/ts/HococoInterfaces';
import ErrorResponse from '@/ts/ErrorResponse';
import { saveAs } from 'file-saver';

@Module({name: 'Documents', namespaced: true})
export default class Documents extends VuexModule {
    public adminDocuments: DocumentsData[] = [];
    public currentDocument: any = [];
    public documentSearchOption: any = '';
    public documentDirOption: any = 'desc';
    public documentColumnOption: any = 'id';
    public previewMediaVisible: boolean = false;
    public previewMedia: any = null;
    public documentProjectOption: any = [];
    public userDocuments: DocumentsData[] = [];
    public activeDocument: any = null;
    public hasDownloadableFiles: boolean = false;

    @Mutation
    public RESET_DOCUMENTS_STORE_STATE(): void {
        this.adminDocuments         = [];
        this.currentDocument        = [];
        this.documentSearchOption   = '';
        this.documentDirOption      = 'desc';
        this.documentColumnOption   = 'id';
        this.previewMediaVisible    = false;
        this.previewMedia           = null;
        this.documentProjectOption  = [];
        this.userDocuments          = [];
        this.activeDocument         = null;
        this.hasDownloadableFiles   = false;
    }

    get getAdminDocuments() {
        return this.adminDocuments;
    }

    get getCurrentDocument() {
        return this.currentDocument;
    }

    get getPreviewMedia() {
        return this.previewMedia;
    }

    get getPreviewMediaVisible() {
        return this.previewMediaVisible;
    }

    get getDocumentSearchOption() {
        return this.documentSearchOption;
    }

    get getDocumentProjectOption() {
        return this.documentProjectOption;
    }

    get getDocumentDirOption() {
        return this.documentDirOption;
    }

    get getDocumentColumnOption() {
        return this.documentColumnOption;
    }

    get getUserDocuments() {
        return this.userDocuments;
    }

    get getActiveDocument() {
        return this.activeDocument;
    }

    get getDocument() {
        return (documentId: number) => {
            return this.userDocuments.find((doc: any) => doc.id === documentId);
        };
    }

    get downloadFilesActive() {
        return this.hasDownloadableFiles;
    }

    @Action
    public FETCH_ADMIN_DOCUMENTS(data: any) {
        const projectIds = data.project.map((project: any) => project.id);

        axios.get(Endpoints.Admin.Documents.DOCUMENTS(data.search, data.column, data.dir, projectIds))
            .then((response) => {
                if (response.data !== null) {
                    store.commit(Mutations.SET_ADMIN_DOCUMENTS, response.data.data);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_CURRENT_DOCUMENT(data: any) {
        axios.get(Endpoints.Admin.Documents.SINGLE_DOCUMENT(data.id))
            .then((response) => {
                if (response.data !== null && typeof response.data.data !== 'undefined') {
                    store.commit(Mutations.SET_CURRENT_DOCUMENT, response.data.data);

                    if (response.data.data.type === 'document') {
                        store.dispatch(Actions.SHOW_FORM, 'edit-document');
                    } else {
                        store.dispatch(Actions.SHOW_FORM, 'edit-contract');
                    }
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_NEW_DOCUMENT(data: any) {
        const headers = {
            headers: {
                'Content-Type': 'multipart-data',
            },
        };

        const requestPayload = new FormData();
        requestPayload.append('projects', data.projects);
        requestPayload.append('buildings', data.buildings);
        requestPayload.append('properties', data.properties);
        requestPayload.append('internal', data.internal);
        requestPayload.append('forCorporation', data.forCorporation);

        data.files.forEach((file: any, index: number) => {
            requestPayload.append(`documentFiles[${index}][lang]`, file.lang);
            requestPayload.append(`documentFiles[${index}][file]`, file.content);
        });

        data.titles.forEach((title: any, index: number) => {
            requestPayload.append(`titles[${index}][lang]`, title.lang);
            requestPayload.append(`titles[${index}][content]`, title.content);
        });

        data.descriptions.forEach((description: any, index: number) => {
            requestPayload.append(`descriptions[${index}][lang]`, description.lang);
            requestPayload.append(`descriptions[${index}][content]`, description.content);
        });

        if (data.categories.length > 0) {
            for (const category of data.categories) {
                requestPayload.append('categories[]', category);
            }
        }

        axios.post(Endpoints.Admin.Documents.ADD_DOCUMENT, requestPayload, headers)
            .then((response) => {
                if (response.data !== null) {
                    if (data.fromLanding) {
                        store.commit(Mutations.ADD_PROPERTY_DOCUMENT, response.data.data);
                    }

                    store.commit(Mutations.SET_ADMIN_DOCUMENT, response.data.data);
                }

                store.dispatch(Actions.HIDE_FORM);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_NEW_CONTRACT(data: any) {
        const headers = {
            headers: {
                'Content-Type': 'multipart-data',
            },
        };

        const requestPayload = new FormData();
        requestPayload.append('deadline', data.deadline);
        requestPayload.append('projects', data.projects);
        requestPayload.append('buildings', data.buildings);
        requestPayload.append('properties', data.properties);
        requestPayload.append('internal', data.internal);
        requestPayload.append('forCorporation', data.forCorporation);

        data.files.forEach((file: any, index: number) => {
            requestPayload.append(`documentFiles[${index}][lang]`, file.lang);
            requestPayload.append(`documentFiles[${index}][file]`, file.content);
        });

        data.titles.forEach((title: any, index: number) => {
            requestPayload.append(`titles[${index}][lang]`, title.lang);
            requestPayload.append(`titles[${index}][content]`, title.content);
        });

        data.descriptions.forEach((description: any, index: number) => {
            requestPayload.append(`descriptions[${index}][lang]`, description.lang);
            requestPayload.append(`descriptions[${index}][content]`, description.content);
        });

        if (data.categories.length > 0) {
            for (const category of data.categories) {
                requestPayload.append('categories[]', category);
            }
        }

        axios.post(Endpoints.Admin.Documents.ADD_CONTRACT, requestPayload, headers)
            .then((response) => {
                if (response.data !== null) {
                    if (data.fromLanding) {
                        store.commit(Mutations.ADD_PROPERTY_CONTRACT, response.data.data);
                    }

                    store.commit(Mutations.SET_ADMIN_DOCUMENT, response.data.data);
                }

                store.dispatch(Actions.HIDE_FORM);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public EDIT_DOCUMENT(data: any) {
        const headers = {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        };

        const requestPayload = new FormData();
        requestPayload.append('_method', 'PATCH');
        requestPayload.append('internal', data.internal);

        data.files.forEach((file: any, index: number) => {
            requestPayload.append(`documentFiles[${index}][lang]`, file.lang);
            requestPayload.append(`documentFiles[${index}][file]`, file.content.hasOwnProperty('id') ? '' : file.content);
        });

        data.titles.forEach((title: any, index: number) => {
            requestPayload.append(`titles[${index}][lang]`, title.lang);
            requestPayload.append(`titles[${index}][content]`, title.content);
        });

        data.descriptions.forEach((description: any, index: number) => {
            requestPayload.append(`descriptions[${index}][lang]`, description.lang);
            requestPayload.append(`descriptions[${index}][content]`, description.content);
        });

        if (data.categories.length > 0) {
            for (const category of data.categories) {
                requestPayload.append('categories[]', category);
            }
        }

        axios.post(Endpoints.Admin.Documents.EDIT_DOCUMENT(data.id), requestPayload, headers)
            .then(() => {
                store.dispatch(Actions.FETCH_ADMIN_DOCUMENTS, {
                    dir: store.getters['Documents/getDocumentDirOption'],
                    column: store.getters['Documents/getDocumentColumnOption'],
                    search: store.getters['Documents/getDocumentSearchOption'],
                    project: store.getters['Documents/getDocumentProjectOption'],
                });
                store.dispatch(Actions.HIDE_FORM);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public EDIT_CONTRACT(data: any) {
        const headers = {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        };

        const requestPayload = new FormData();
        requestPayload.append('_method', 'PATCH');
        requestPayload.append('deadline', data.deadline);
        requestPayload.append('internal', data.internal);

        data.files.forEach((file: any, index: number) => {
            requestPayload.append(`contractFiles[${index}][lang]`, file.lang);
            requestPayload.append(`contractFiles[${index}][file]`, file.content.hasOwnProperty('id') ? '' : file.content);
        });

        data.titles.forEach((title: any, index: number) => {
            requestPayload.append(`titles[${index}][lang]`, title.lang);
            requestPayload.append(`titles[${index}][content]`, title.content);
        });

        data.descriptions.forEach((description: any, index: number) => {
            requestPayload.append(`descriptions[${index}][lang]`, description.lang);
            requestPayload.append(`descriptions[${index}][content]`, description.content);
        });

        if (data.categories.length > 0) {
            for (const category of data.categories) {
                requestPayload.append('categories[]', category);
            }
        }

        axios.post(Endpoints.Admin.Documents.EDIT_CONTRACT(data.id), requestPayload, headers)
            .then(() => {
                store.dispatch(Actions.FETCH_ADMIN_DOCUMENTS, {
                    dir: store.getters['Documents/getDocumentDirOption'],
                    column: store.getters['Documents/getDocumentColumnOption'],
                    search: store.getters['Documents/getDocumentSearchOption'],
                    project: store.getters['Documents/getDocumentProjectOption'],
                });
                store.dispatch(Actions.HIDE_FORM);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public DELETE_DOCUMENT(data: any) {
        axios.delete(Endpoints.Admin.Documents.SINGLE_DOCUMENT(data.id))
            .then(() => {
                if (typeof data.fromLanding !== 'undefined') {
                    store.commit(Mutations.REMOVE_DOCUMENT_FROM_UNIT, data.id);
                } else {
                    store.commit(Mutations.REMOVE_CURRENT_DOCUMENT, data.id);
                }

                store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_PREVIEW_MEDIA(data: any) {
        axios.get(Endpoints.Admin.Documents.PREVIEW_MEDIA(data.id))
            .then((response) => {
                if (data.action === 'preview') {
                    store.commit(Mutations.SET_PREVIEW_MEDIA, response.data);
                    store.commit(Mutations.SET_PREVIEW_MEDIA_VISIBILITY, true);
                } else {
                    saveAs(`data:application/pdf;base64,${response.data.base64}`, response.data.original_filename);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_DOCUMENT_MEDIA_PREVIEW(mediaId: number) {
        axios.get(Endpoints.Admin.Documents.PREVIEW_DOCUMENT_MEDIA(mediaId))
            .then((response) => {
                const byteCharacters = atob(response.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const file = new Blob([byteArray], { type: 'application/pdf;base64' });
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    // User
    @Action
    public FETCH_USER_DOCUMENTS(tab: string = 'All') {
        axios.get(Endpoints.Documents.DOCUMENTS_LIST(tab))
            .then((response) => {
                if (response.data !== null) {
                    store.commit(Mutations.SET_USER_DOCUMENTS, {users: response.data.data, tab});
                }
            })
            .catch((error) => {
                if (error.status === 401) {
                    store.dispatch(Actions.HANDLE_401);
                }
            }).then(() => {
            });

        return Promise.resolve();
    }

    @Action
    public FETCH_ACTIVE_DOCUMENT(data: any) {
        axios.get(Endpoints.Documents.ACTIVE_DOCUMENT(data.id))
            .then((response) => {
                if (response.data !== null) {
                    store.commit(Mutations.SET_ACTIVE_DOCUMENT, response.data.data);
                    // if additional parameters passsed(name, view, viewMode), route/view must be changed
                    if (typeof data.name !== 'undefined') {
                        router.push({
                            name: data.name,
                            params: {
                                documentId: data.id.toString(),
                            },
                        });
                    }
                    if (typeof data.view !== 'undefined' && typeof data.viewMode !== 'undefined') {
                        store.commit(Mutations.SET_VIEW, {view : data.view, viewMode: data.viewMode});
                    }
                }
            })
            .catch((error) => {
                if (error.status === 401) {
                    store.dispatch(Actions.HANDLE_401);
                }
            }).then(() => {
            });

        return Promise.resolve();
    }

    @Action
    public SIGN_CONTRACT(documentId: number) {
        const url = Endpoints.Documents.SIGN_DOCUMENT(documentId);

        axios.post(url)
            .then(() => {
                store.commit(Mutations.MARK_DOCUMENT_AS_SIGNED, documentId);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });

        return Promise.resolve();
    }

    @Action
    public MARK_ACTIVE_DOCUMENT_AS_SEEN(documentId: number) {
        const url = Endpoints.Documents.MARK_ACTIVE_DOCUMENT_AS_SEEN(documentId);

        axios.put(url)
            .then(() => {
                store.commit(Mutations.MARK_DOCUMENT_AS_SEEN, documentId);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Mutation
    public SET_ADMIN_DOCUMENTS(documentData: DocumentDataResponse[]): void {
        this.adminDocuments = [];

        for (const document of documentData) {
            this.adminDocuments.push(new DocumentsData(
                document.id,
                document.type,
                document.title,
                document.requiresSignature,
                document.isSigned,
                document.deadline,
                document.processed,
                document.read,
                document.source,
                document.createdAt,
                document.updatedAt,
                document.signatures,
                document.translations,
                document.categories,
                document.media,
                document.projects,
                document.buildings,
                document.variants,
                document.properties,
            ));
        }
    }

    @Mutation
    public SET_ADMIN_DOCUMENT(documentData: DocumentDataResponse): void {
        this.adminDocuments.unshift(new DocumentsData(
            documentData.id,
            documentData.type,
            documentData.title,
            documentData.requiresSignature,
            documentData.isSigned,
            documentData.deadline,
            documentData.processed,
            documentData.read,
            documentData.source,
            documentData.createdAt,
            documentData.updatedAt,
            documentData.signatures,
            documentData.translations,
            documentData.categories,
            documentData.media,
            documentData.projects,
            documentData.buildings,
            documentData.variants,
            documentData.properties,
        ));
    }

    @Mutation
    public SET_CURRENT_DOCUMENT(document: any): void {
        this.currentDocument = document;
    }

    @Mutation
    public SET_DOCUMENT_SEARCH_OPTION(search: any) {
        this.documentSearchOption = search;
    }

    @Mutation
    public SET_DOCUMENT_COLUMN_OPTION(column: string) {
        this.documentColumnOption = column;
    }

    @Mutation
    public SET_DOCUMENT_DIR_OPTION(dir: string) {
        this.documentDirOption = dir;
    }

    @Mutation
    public SET_DOCUMENT_PROJECT_OPTION(project: string) {
        this.documentProjectOption = project;
    }

    @Mutation
    public SET_PREVIEW_MEDIA(data: any) {
        this.previewMedia = data;
    }

    @Mutation
    public SET_PREVIEW_MEDIA_VISIBILITY(visibile: boolean) {
        this.previewMediaVisible = visibile;
    }

    @Mutation
    public REMOVE_CURRENT_DOCUMENT(id: number) {
        this.adminDocuments = this.adminDocuments.filter((doc: any) => {
            return doc.id !== id;
        });
    }

    // User
    @Mutation
    public SET_USER_DOCUMENTS(data: any): void {
        this.userDocuments = [];
        for (const document of data.users) {
            this.userDocuments.push(new DocumentsData(
                document.id,
                document.type,
                document.title,
                document.requiresSignature,
                document.isSigned,
                document.deadline,
                document.processed,
                document.read,
                document.source,
                document.createdAt,
                document.updatedAt,
                document.signatures,
                document.translations,
            ));
        }
    }

    @Mutation
    public SET_ACTIVE_DOCUMENT(document: any): void {
            this.activeDocument = !document ? document : new DocumentsData(
                document.id,
                document.type,
                document.title,
                document.requiresSignature,
                document.isSigned,
                document.deadline,
                document.processed,
                document.read,
                document.source,
                document.createdAt,
                document.updatedAt,
                document.signatures,
                document.translations,
            );
    }

    @Mutation
    public MARK_DOCUMENT_AS_SIGNED(documentId: number) {
        for (const doc of this.userDocuments) {
            if (doc.id === documentId) {
                doc.isSigned = true;
            }
        }
    }

    @Mutation
    public MARK_DOCUMENT_AS_SEEN(documentId: number) {
        for (const doc of this.userDocuments) {
            if (doc.id === documentId) {
                doc.read = true;
            }
        }
    }
}
